import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  ButtonBase,
  Collapse,
  IconButton,
  InputBase,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import Iconify from 'src/components/iconify';
import { usePathname, useRouter } from 'src/routes/hooks';
import { useCurrentUserQuery } from 'src/context/api/auth';
import { useGetWorkspacesQuery } from 'src/context/api/workspaces';
import { setCurrentWorkspace, useCurrentWorkspace } from 'src/context/reducers/app-settings';
import {
  setFilterAdvertizerPLA, setFilterAveragePositionPLA, setFilterBrandPLA,
  setFilterCountry, setFilterDomainPLA, setFilterImpressionSharePLA,
  setFilterKeyword, setFilterLoadedKeyword, setFilterPricingRangePLA,
  setFilterRatingRangePLA, setNotificationBrandInNonBrandCampaigns,
  setNotificationBrandInNonBrandCampaignsFilterEnabled,
  setNotificationBrandInfringement, setNotificationBrandInfringementNotification,
  setNotificationBrandInfringementNotificationTXT, setNotificationBrandInfringementTXT,
} from 'src/context/reducers/filter-settings/filterSlice';
import { canCreateWorkspace } from 'src/utils/workspace-roles';
import { NAV } from '../config-layout';

// ----------------------------------------------------------------------
// Design tokens (from Figma — Rebranding Signal 2026 Q2)

const C = {
  bg: '#17191a',
  text: '#FFFFFF',
  subheader: '#637381',
  headerBorder: 'rgba(216, 227, 240, 0.16)',
  footerBorder: 'rgba(145, 158, 171, 0.20)',
  accent: '#ECFF9F',
  activeBg: 'rgba(236, 255, 159, 0.08)',
  itemHover: 'rgba(255, 255, 255, 0.04)',
};

const FONT = '"Public Sans", sans-serif';

// Internal types (normalized from navData)
type NavChild = { label: string; path: string; activePaths?: string[] };
type NavItem = {
  label: string;
  iconNode?: React.ReactNode;
  path?: string;
  children?: NavChild[];
  activePaths?: string[];
  locked?: boolean;
  disabled?: boolean;
};
type Section = { subheader: string; locked?: boolean; items: NavItem[] };

// Normalize navData (title/icon/children → label/iconNode/children)
function normalizeNavData(navData: any[]): Section[] {
  return (navData || []).map((section: any) => ({
    subheader: section.subheader,
    locked: section.locked,
    items: (section.items || []).map((item: any) => ({
      label: item.title,
      iconNode: item.icon,
      path: item.path,
      activePaths: item.activePaths,
      locked: item.locked,
      disabled: item.disabled,
      children: item.children?.length
        ? item.children.map((c: any) => ({
            label: c.title,
            path: c.path,
            activePaths: c.activePaths,
          }))
        : undefined,
    })),
  }));
}

// ----------------------------------------------------------------------
// Sub-components

type CommonProps = { expanded: boolean };

// Workspace header — single render, border width animates with nav expansion
function WorkspaceHeader({ expanded, onDropdownChange }: CommonProps & { onDropdownChange?: (open: boolean) => void }) {
  const EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';
  const anchorRef = useRef<HTMLElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [search, setSearch] = useState('');

  const dispatch = useDispatch();
  const router = useRouter();
  const [, setSearchParams] = useSearchParams();
  const workspace: any = useCurrentWorkspace();
  const { data: user } = useCurrentUserQuery();
  const { data: workspaces } = useGetWorkspacesQuery(null) as any;
  const sorted = useMemo(
    () => (workspaces ? [...workspaces].sort((a: any, b: any) => a.name.localeCompare(b.name)) : []),
    [workspaces]
  );

  const favicon = (ws: any) =>
    ws?.name?.includes('Demo')
      ? '/assets/logo.png'
      : `https://www.google.com/s2/favicons?domain=${ws?.url}&sz=64`;

  const handleOpen = () => { setMenuOpen(true); onDropdownChange?.(true); };
  const handleClose = () => { setMenuOpen(false); onDropdownChange?.(false); setSearch(''); };

  const handleSelect = (item: any) => {
    handleClose();
    setSearchParams({ workspace: item.id.toString() });
    dispatch(setFilterKeyword([]));
    dispatch(setFilterCountry([]));
    dispatch(setFilterBrandPLA([]));
    dispatch(setFilterDomainPLA([]));
    dispatch(setFilterAdvertizerPLA([]));
    dispatch(setNotificationBrandInfringementNotification([]));
    dispatch(setNotificationBrandInfringementNotificationTXT([]));
    dispatch(setNotificationBrandInNonBrandCampaignsFilterEnabled(false));
    dispatch(setNotificationBrandInNonBrandCampaigns(false));
    dispatch(setFilterRatingRangePLA([0, 5]));
    dispatch(setFilterPricingRangePLA([0, 50000]));
    dispatch(setFilterLoadedKeyword([]));
    dispatch(setFilterImpressionSharePLA([0, 100]));
    dispatch(setFilterAveragePositionPLA([0, 50]));
    dispatch(setNotificationBrandInfringement(false));
    dispatch(setNotificationBrandInfringementTXT(false));
    dispatch(setCurrentWorkspace(item));
    router.push('/brand/search-performance/overview?workspace=' + item.id.toString());
  };

  const filtered = useMemo(
    () => sorted.filter((ws: any) => ws.name.toLowerCase().includes(search.toLowerCase())),
    [sorted, search]
  );
  const others = filtered.filter((ws: any) => ws.id !== workspace?.id);

  return (
    <>
      <Box
        ref={anchorRef}
        onClick={handleOpen}
        sx={{
          height: 52,
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          px: '8px',
          py: '8px',
          cursor: 'pointer',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            width: expanded ? '100%' : 40,
            overflow: 'hidden',
            px: '8px',
            py: '6px',
            border: `1px solid ${C.headerBorder}`,
            borderRadius: '8px',
            flexShrink: 0,
            transition: `width 0.4s ${EASING}`,
          }}
        >
          <Box
            component="img"
            src={favicon(workspace)}
            alt=""
            sx={{ width: 22, height: 22, borderRadius: '5px', objectFit: 'cover', flexShrink: 0 }}
          />
          <Typography
            sx={{
              color: C.text,
              fontFamily: FONT,
              fontSize: 14,
              fontWeight: 400,
              lineHeight: '22px',
              flex: 1,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {workspace?.name ?? '—'}
          </Typography>
          <Iconify icon="hugeicons:arrow-down-01" width={18} sx={{ color: C.text, flexShrink: 0, ml: 'auto' }} />
        </Box>
      </Box>

      <Popover
        open={menuOpen}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            bgcolor: '#1c252e',
            borderRadius: '8px',
            width: NAV.W_VERTICAL_EXPANDED - 16,
            mt: '-4px',
            ml: '-8px',
            overflow: 'hidden',
            boxShadow: '0px 2px 5px 0px rgba(0,0,0,0.16)',
          },
        }}
      >
        {/* Search bar */}
        <Box sx={{ bgcolor: 'rgba(145,158,171,0.2)', height: 44, display: 'flex', alignItems: 'center', px: 1.5, gap: 1 }}>
          <InputBase
            autoFocus
            placeholder={`Search accounts (${sorted.length})`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              flex: 1,
              color: '#919eab',
              fontFamily: FONT,
              fontSize: 14,
              fontWeight: 500,
              lineHeight: 1.4,
              '& input::placeholder': { color: '#919eab', opacity: 1 },
            }}
            inputProps={{ style: { padding: 0 } }}
          />
        </Box>

        {/* Workspace list */}
        <Box
          sx={{
            p: 1,
            maxHeight: 320,
            overflowY: 'auto',
            '&::-webkit-scrollbar': { width: 3 },
            '&::-webkit-scrollbar-thumb': { bgcolor: '#637381', borderRadius: 6 },
            '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
          }}
        >
          {/* Active workspace card */}
          {workspace && (
            <Box
              sx={{
                bgcolor: 'rgba(236,255,159,0.08)',
                border: '0.5px solid #ecff9f',
                borderRadius: '8px',
                mb: '4px',
              }}
            >
              {/* Name row */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1.5, borderBottom: '1px solid rgba(145,158,171,0.1)' }}>
                <Box
                  component="img"
                  src={favicon(workspace)}
                  alt=""
                  sx={{ width: 20, height: 20, borderRadius: '5px', objectFit: 'cover', flexShrink: 0 }}
                />
                <Typography sx={{ flex: 1, color: 'white', fontFamily: FONT, fontSize: 14, fontWeight: 500, lineHeight: '23px' }}>
                  {workspace.name}
                </Typography>
                <Iconify icon="eva:checkmark-fill" width={16} sx={{ color: 'white', flexShrink: 0 }} />
              </Box>
              {/* Workspace settings link — hidden for customer role */}
              {user?.role?.toLowerCase() !== 'customer' && (
                <Box
                  onClick={() => { handleClose(); router.push(`/workspaces/${workspace.id}/setup`); }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: '6px',
                    pb: '4px',
                    cursor: 'pointer',
                    borderRadius: '6px',
                    '&:hover': { bgcolor: C.itemHover },
                  }}
                >
                  <Iconify icon="hugeicons:settings-04" width={16} sx={{ color: 'white', flexShrink: 0 }} />
                  <Typography sx={{ color: 'white', fontFamily: FONT, fontSize: 14, fontWeight: 500, lineHeight: 1.4 }}>
                    Workspace settings
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Other workspaces */}
          {others.map((ws: any) => (
            <Box
              key={ws.id}
              onClick={() => handleSelect(ws)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 1.5,
                borderRadius: '8px',
                cursor: 'pointer',
                '&:hover': { bgcolor: C.itemHover },
              }}
            >
              <Box
                component="img"
                src={favicon(ws)}
                alt=""
                sx={{ width: 20, height: 20, borderRadius: '5px', objectFit: 'cover', flexShrink: 0 }}
              />
              <Box sx={{ display: 'flex', flex: 1, alignItems: 'center', justifyContent: 'space-between', gap: '6px', overflow: 'hidden' }}>
                <Typography sx={{ color: 'white', fontFamily: FONT, fontSize: 14, fontWeight: 500, lineHeight: '23px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {ws.name}
                </Typography>
                {ws.is_pitch && (
                  <Box sx={{ bgcolor: 'rgba(236,255,159,0.12)', px: 0.75, py: 0.5, borderRadius: '6px', flexShrink: 0 }}>
                    <Typography sx={{ color: '#ecff9f', fontSize: 13, fontWeight: 500, fontFamily: '"Figtree", sans-serif', lineHeight: 1 }}>
                      Pitch
                    </Typography>
                  </Box>
                )}
              </Box>
              {/* Spacer to align with active checkmark */}
              <Box sx={{ width: 16, height: 16, flexShrink: 0 }} />
            </Box>
          ))}
        </Box>

        {/* Footer: Add workspace — only for the roles the API lets create one */}
        {canCreateWorkspace(user?.role) && (
        <Box sx={{ borderTop: '1px solid rgba(145,158,171,0.2)', px: 1.5, py: 1 }}>
          <Box
            onClick={() => { handleClose(); router.push('/workspaces/add'); }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              px: 1.5,
              py: 0.75,
              bgcolor: 'rgba(145,158,171,0.12)',
              borderRadius: '8px',
              cursor: 'pointer',
              '&:hover': { bgcolor: 'rgba(145,158,171,0.2)' },
            }}
          >
            <Iconify icon="hugeicons:plus-sign" width={16} sx={{ color: 'white' }} />
            <Typography sx={{ color: 'white', fontFamily: FONT, fontSize: 14, fontWeight: 500, lineHeight: 1.4, whiteSpace: 'nowrap' }}>
              Add workspace
            </Typography>
          </Box>
        </Box>
        )}
      </Popover>
    </>
  );
}

// Section separator — arrow-down at x=20 (same as nav icons) + line extending right
// Arrow always visible in compact; line becomes visible as nav expands
function SectionHeader({ label, expanded, locked }: { label: string; expanded: boolean; locked?: boolean }) {
  return (
    <Box sx={{ pl: '20px', height: 25, display: 'flex', alignItems: 'center' }}>
      {expanded ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Typography
            sx={{
              color: C.subheader,
              fontFamily: FONT,
              fontSize: 11,
              fontWeight: 700,
              lineHeight: '16.5px',
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
            }}
          >
            {label}
          </Typography>
          {locked && (
            <Iconify icon="material-symbols:lock-outline" width={13} sx={{ color: C.subheader, flexShrink: 0 }} />
          )}
        </Box>
      ) : (
        <Box sx={{ width: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Iconify icon="mdi:chevron-down" width={10} sx={{ color: 'rgba(99, 115, 129, 1)' }} />
        </Box>
      )}
    </Box>
  );
}

// L2 item — handles both compact and expanded states.
// Icon is always at px=20px so its position never moves between states.
function L2Item({
  item,
  expanded,
  active,
  hasOpenChild,
  isOpen,
  onClick,
}: {
  item: NavItem;
  expanded: boolean;
  active: boolean;
  hasOpenChild: boolean;
  isOpen: boolean;
  onClick: () => void;
}) {
  const highlight = active || hasOpenChild;
  const locked = item.locked;
  const disabled = item.disabled;
  return (
    <ButtonBase
      onClick={disabled ? undefined : onClick}
      disableRipple={disabled}
      sx={{
        width: expanded ? 'calc(100% - 28px)' : NAV.W_VERTICAL - 20,
        height: 38,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: expanded ? '8px' : 0,
        mx: expanded ? '14px' : '10px',
        px: expanded ? '6px' : '10px',
        borderRadius: '4px',
        cursor: disabled ? 'default' : 'pointer',
        bgcolor: !item.children && active ? C.activeBg : 'transparent',
        transition: 'background-color 0.15s ease',
        '&:hover': disabled ? {} : { bgcolor: C.itemHover },
      }}
    >
      {/* Icon at px=20 — same position in compact and expanded */}
      <Box sx={{ width: 16, height: 16, flexShrink: 0, color: disabled ? C.subheader : (highlight ? C.accent : C.text), display: 'flex', alignItems: 'center', justifyContent: 'center', '& svg': { width: 16, height: 16 } }}>
        {item.iconNode}
      </Box>

      {expanded && (
        <>
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.75, overflow: 'hidden' }}>
            <Typography
              sx={{
                textAlign: 'left',
                color: disabled ? C.subheader : C.text,
                fontFamily: FONT,
                fontSize: 14,
                fontWeight: 500,
                lineHeight: '22px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minWidth: 0,
              }}
            >
              {item.label}
            </Typography>
            {locked && (
              <Iconify icon="material-symbols:lock-outline" width={14} sx={{ color: C.text, flexShrink: 0 }} />
            )}
          </Box>
          {item.children && (
            <Iconify
              icon="hugeicons:arrow-right-01"
              width={16}
              sx={{
                color: C.text,
                flexShrink: 0,
                transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease',
              }}
            />
          )}
        </>
      )}
    </ButtonBase>
  );
}

// L3 item — expanded mode only
function L3Item({ child, active, onClick }: { child: NavChild; active: boolean; onClick: () => void }) {
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        width: '100%',
        height: 30,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        px: '8px',
        py: '4px',
        borderRadius: '6px',
        bgcolor: active ? C.activeBg : 'transparent',
        transition: 'background-color 0.15s ease',
        '&:hover': { bgcolor: active ? C.activeBg : C.itemHover },
      }}
    >
      <Typography sx={{ color: active ? C.accent : C.text, fontFamily: FONT, fontSize: 14, fontWeight: 500, lineHeight: '22px' }}>
        {child.label}
      </Typography>
    </ButtonBase>
  );
}

// Footer
// Compact: avatar only, p-12, centered, no border
// Expanded: border-top + avatar left + settings/toggle right
function Footer({ expanded, pinned = false, onTogglePin }: CommonProps & { pinned?: boolean; onTogglePin?: () => void }) {
  const router = useRouter();
  const { data: user } = useCurrentUserQuery();
  const role = user?.role;
  const isAdmin = ['admin', 'brand specialist', 'consultant'].includes(role);

  const avatar = (size: number) => (
    <Box
      component="img"
      src="/assets/logo_crossbrand.png"
      alt=""
      sx={{ width: `${size}px`, height: `${size}px`, minWidth: `${size}px`, borderRadius: '8px', objectFit: 'cover', bgcolor: 'rgba(255,255,255,0.04)' }}
    />
  );

  const iconBtn = (icon: string, onClick?: () => void, sx?: object) => (
    <IconButton
      onClick={onClick}
      sx={{ p: '8px', borderRadius: '6px', color: C.text, '&:hover': { bgcolor: C.itemHover }, ...sx }}
    >
      <Iconify icon={icon} width={16} />
    </IconButton>
  );

  return (
    <Box
      sx={{
        boxShadow: expanded ? `inset 0 1px 0 ${C.footerBorder}` : 'none',
        px: '12px',
        height: 56,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0,
      }}
    >
      {avatar(32)}
      {expanded && (
        <Stack direction="row" alignItems="center" gap="12px">
          {isAdmin && iconBtn('hugeicons:settings-01', () => router.push('/admin/cockpit'))}
          {iconBtn('hugeicons:layout-right', onTogglePin, {
            bgcolor: pinned ? 'rgba(236, 255, 159, 0.12)' : undefined,
            color: pinned ? C.accent : C.text,
          })}
        </Stack>
      )}
    </Box>
  );
}

// ----------------------------------------------------------------------
// Main component

type Props = {
  expanded: boolean;
  navData: any;
  pinned?: boolean;
  onTogglePin?: () => void;
  onDropdownChange?: (open: boolean) => void;
};

export default function NavContent({ expanded, navData, pinned = false, onTogglePin, onDropdownChange }: Props) {
  const [isExpanded, setIsExpanded] = useState(false);
  const pathname = usePathname();
  const router = useRouter();

  const sections = useMemo(() => normalizeNavData(navData), [navData]);

  useEffect(() => {
    setIsExpanded(expanded);
  }, [expanded]);

  const isActive = (path?: string, activePaths?: string[]) => {
    if (activePaths?.length) return activePaths.some(p => pathname === p || pathname.startsWith(`${p}/`));
    return !!path && (pathname === path || pathname.startsWith(`${path}/`));
  };

  const initialOpen = useMemo(() => {
    const map: Record<string, boolean> = {};
    sections.forEach((s) =>
      s.items.forEach((item) => {
        if (item.children?.some((c) => isActive(c.path, c.activePaths))) {
          map[item.label] = true;
        }
      })
    );
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, sections]);

  const [openMap, setOpenMap] = useState<Record<string, boolean>>(initialOpen);

  useEffect(() => {
    setOpenMap((prev) => ({ ...prev, ...initialOpen }));
  }, [initialOpen]);

  const handleItemClick = (item: NavItem) => {
    if (item.children) {
      setOpenMap((prev) => ({ ...prev, [item.label]: !prev[item.label] }));
    } else if (item.path) {
      router.push(item.path);
    }
  };

  return (
    <Stack sx={{ width: NAV.W_VERTICAL_EXPANDED, height: '100%', bgcolor: C.bg, boxShadow: '4px 0 10px 0 rgba(0, 0, 0, 0.25)' }}>
      <WorkspaceHeader expanded={expanded} onDropdownChange={onDropdownChange} />

      {/* Scrollable nav items */}
      <Stack
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          pt: '12px',
          pb: '12px',
          gap: '8px',
          '&::-webkit-scrollbar': { width: 0 },
        }}
      >
        {sections.map((section) => (
          <Stack key={section.subheader}>
            <SectionHeader label={section.subheader} expanded={expanded} locked={section.locked} />
            {section.items.map((item) => {
              const active = isActive(item.path, item.activePaths);
              const hasOpenChild = !!item.children?.some((c) => isActive(c.path, c.activePaths));
              const open = !!openMap[item.label];
              return (
                <Box key={item.label}>
                  <L2Item
                    item={item}
                    expanded={expanded}
                    active={active}
                    hasOpenChild={hasOpenChild}
                    isOpen={open}
                    onClick={() => handleItemClick(item)}
                  />
                  {item.children && (
                    <Collapse in={open} timeout={200}>
                      <Box
                        sx={{
                          pl: '36px',
                          pr: '14px',
                          position: 'relative',
                          visibility: expanded ? 'visible' : 'hidden',
                        }}
                      >
                        {/* Per-child indicators — always visible (override parent hidden).
                            Active: accent line segment + background overlay.
                            Inactive: muted line segment. */}
                        {item.children.map((child, index) => (
                          <Box key={`ind-${child.label}`}>
                            {/* Compact only: small overlay centered on icon */}
                            {!expanded && isActive(child.path) && (
                              <Box sx={{
                                position: 'absolute',
                                left: 10,
                                top: index * 30,
                                width: 36,
                                height: 30,
                                borderRadius: '6px',
                                bgcolor: C.activeBg,
                                visibility: 'visible',
                              }} />
                            )}
                            {/* Line segment */}
                            <Box sx={{
                              position: 'absolute',
                              left: 27,
                              top: index * 30,
                              width: 2,
                              height: 30,
                              borderRadius: '1px',
                              bgcolor: isActive(child.path) ? C.accent : 'rgba(255, 255, 255, 0.08)',
                              visibility: 'visible',
                              transition: 'background-color 0.2s ease',
                            }} />
                          </Box>
                        ))}
                        {item.children.map((child) => (
                          <L3Item
                            key={child.label}
                            child={child}
                            active={isActive(child.path, child.activePaths)}
                            onClick={() => router.push(child.path)}
                          />
                        ))}
                      </Box>
                    </Collapse>
                  )}
                </Box>
              );
            })}
          </Stack>
        ))}
      </Stack>


      <Footer expanded={expanded} pinned={pinned} onTogglePin={onTogglePin} />
    </Stack>
  );
}
